import ArrayUtils from 'common/array';
import * as React from 'react';

export type SelectionState<T> = {
  allItems: T[];
  selected: T[];
  setSelected: (t: T) => (s: boolean) => void;
  setShiftSelected: (t: T) => void;
  setSelection: (t: T[]) => void;
  clearSelection: () => void;
};
export const useSelectionState = <T>(
  allItems: T[],
  initial: T[],
): SelectionState<T> => {
  const [selectedItems, setSelectedItems] = React.useState<T[]>(initial || []);
  const [lastSelected, setLastSelected] = React.useState<T | null>(null);

  const setSelectionToSet = (set: Set<T>) =>
    setSelectedItems([...set.values()]);

  const setSelected = (t: T) => (isSelected: boolean) => {
    setLastSelected(t);
    if (isSelected) {
      // Selecting what's already selected (a second long press) is a no-op,
      // never a duplicate: the count, the total, and a create would all
      // double it.
      if (!selectedItems.includes(t)) setSelectedItems([...selectedItems, t]);
    } else {
      setSelectedItems(selectedItems.filter((s) => s !== t));
    }
  };

  const setShiftSelected = (t: T) => {
    const itemSet = new Set(selectedItems);
    if (lastSelected !== null) {
      const startIndex = allItems.indexOf(lastSelected);
      const endIndex = allItems.indexOf(t);

      if (startIndex !== -1 && endIndex !== -1) {
        ArrayUtils.range(startIndex, endIndex).forEach((i) => {
          itemSet.add(allItems[i]);
        });
        itemSet.add(allItems[startIndex]);
        itemSet.add(allItems[endIndex]);
      } else {
        itemSet.add(t);
      }
    } else {
      itemSet.add(t);
    }
    setSelectionToSet(itemSet);
  };

  const setSelection = (t: T[]) => {
    setLastSelected(null);
    setSelectedItems(t);
  };

  const clearSelection = () => {
    setLastSelected(null);
    setSelectedItems([]);
  };

  return {
    allItems,
    selected: selectedItems,
    setSelected,
    setShiftSelected,
    setSelection,
    clearSelection,
  };
};
